/**
 * JSON and NDJSON.
 *
 * Almost every JSON export a business has is a list of records, sometimes
 * wrapped in an envelope — `{ "data": [...] }`, `{ "items": [...] }`,
 * `{ "results": { "rows": [...] } }`. So the job is to find the list, flatten
 * its records into columns, and hand the result to the same table pipeline
 * everything else goes through. One inference implementation, not two.
 *
 * Nested objects are flattened with a dotted heading — `client.name` becomes a
 * column labelled `Client name` — which is what makes a JSON export usable by
 * someone who would not describe anything as nested. Nested *arrays* are not
 * flattened: a column cannot hold a list, so they are left out with a notice
 * rather than stringified into something nobody can bind to sensibly.
 */
import type { Cell } from "./table.js";
import type { Notice } from "./types.js";

export interface JsonTable {
  /** Heading per column, in the order the first records introduced them. */
  headers: string[];
  rows: Cell[][];
  notices: Notice[];
  /** Set when the file was one object rather than a list: the dataset has one row. */
  singleObject: boolean;
}

/** How deep object flattening goes. Below this a document could not use the field anyway. */
const MAX_DEPTH = 4;

/** Keys an envelope hides its list under, most conventional first. */
const ENVELOPE_KEYS = ["data", "items", "results", "records", "rows", "value", "list", "entries"];

export function parseJsonText(text: string, opts: { maxRows?: number } = {}): JsonTable {
  const trimmed = text.trim();
  if (!trimmed) throw new JsonShapeError("This file is empty.");

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    // Not JSON as a whole — try NDJSON, where each line is its own document.
    // This is the shape most export tools and log pipelines produce, and it
    // fails JSON.parse on line two, which is a confusing thing to be told.
    const lines = trimmed.split(/\r?\n/).filter((l) => l.trim());
    const records: unknown[] = [];
    for (const line of lines) {
      try {
        records.push(JSON.parse(line));
      } catch {
        throw new JsonShapeError("We couldn't read this file as JSON. Check that it contains a valid object, or a list of records.");
      }
    }
    if (!records.length) throw new JsonShapeError("We couldn't read this file as JSON. Check that it contains a valid object, or a list of records.");
    return tabulate(records, opts, false);
  }

  const { list, singleObject, notices } = findList(parsed);
  const table = tabulate(list, opts, singleObject);
  return { ...table, notices: [...notices, ...table.notices] };
}

export class JsonShapeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "JsonShapeError";
  }
}

/**
 * Finds the list of records inside whatever was parsed.
 *
 * An array is the list. An object holding exactly one array of objects is an
 * envelope, whatever its key is called, and that is a safe rule: there is
 * nothing else the list could be. An object holding several is ambiguous, so
 * the conventional key names are tried in order before giving up and treating
 * the object itself as a single record.
 */
function findList(parsed: unknown): { list: unknown[]; singleObject: boolean; notices: Notice[] } {
  if (Array.isArray(parsed)) return { list: parsed, singleObject: false, notices: [] };
  if (!isRecord(parsed)) throw new JsonShapeError("We couldn't read this file as JSON. Check that it contains an object, or a list of records.");

  const arrays = Object.entries(parsed).filter(([, v]) => Array.isArray(v) && v.length > 0 && isRecord((v as unknown[])[0]));
  if (arrays.length === 1) {
    const [key, value] = arrays[0]!;
    return { list: value as unknown[], singleObject: false, notices: [{ level: "info", message: `The records were found under “${key}”.` }] };
  }
  if (arrays.length > 1) {
    const preferred = ENVELOPE_KEYS.map((k) => arrays.find(([key]) => key.toLowerCase() === k)).find(Boolean);
    if (preferred) {
      return {
        list: preferred[1] as unknown[],
        singleObject: false,
        notices: [
          {
            level: "ask",
            message: `This file contains more than one list of records. The one under “${preferred[0]}” has been used — ${arrays
              .filter(([k]) => k !== preferred[0])
              .map(([k]) => `“${k}”`)
              .join(", ")} ${arrays.length > 2 ? "were" : "was"} left out.`
          }
        ]
      };
    }
  }
  // One object, one row. An invoice exported on its own looks exactly like this.
  return { list: [parsed], singleObject: true, notices: [] };
}

function tabulate(records: unknown[], opts: { maxRows?: number }, singleObject: boolean): JsonTable {
  const max = opts.maxRows ?? Number.POSITIVE_INFINITY;
  const kept = records.slice(0, max);
  const notices: Notice[] = [];
  if (records.length > kept.length) {
    notices.push({ level: "warn", message: `This file has ${records.length.toLocaleString("en-GB")} records. The first ${kept.length.toLocaleString("en-GB")} have been brought in.` });
  }

  const headers: string[] = [];
  const index = new Map<string, number>();
  const flattened: Array<Map<string, Cell>> = [];
  const skippedLists = new Set<string>();

  for (const record of kept) {
    const flat = new Map<string, Cell>();
    if (isRecord(record)) flatten(record, "", flat, skippedLists, 0);
    // A list of bare values — numbers, strings — is a single-column table. It
    // happens, and refusing it would be pedantic.
    else flat.set("value", scalar(record));
    for (const key of flat.keys()) {
      if (!index.has(key)) {
        index.set(key, headers.length);
        headers.push(key);
      }
    }
    flattened.push(flat);
  }

  if (skippedLists.size) {
    notices.push({
      level: "info",
      message: `Some fields in this file hold lists of their own (${[...skippedLists].slice(0, 3).map((k) => `“${k}”`).join(", ")}${skippedLists.size > 3 ? " and others" : ""}). A document column cannot hold a list, so those have been left out.`
    });
  }

  const rows = flattened.map((flat) => headers.map((h) => flat.get(h) ?? null));
  return { headers, rows, notices, singleObject };
}

function flatten(value: Record<string, unknown>, prefix: string, out: Map<string, Cell>, skipped: Set<string>, depth: number): void {
  for (const [key, v] of Object.entries(value)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (Array.isArray(v)) {
      if (v.length) skipped.add(path);
      continue;
    }
    if (isRecord(v)) {
      if (depth + 1 >= MAX_DEPTH) continue;
      flatten(v, path, out, skipped, depth + 1);
      continue;
    }
    out.set(path, scalar(v));
  }
}

function scalar(v: unknown): Cell {
  if (v === null || v === undefined) return null;
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return v;
  // A Date cannot survive JSON, so anything left is an object we decided not to
  // walk into; its text form is the honest answer.
  return String(v);
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
